import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Plus } from 'lucide-react';
import { AssignmentCreateModal, AssignmentReturnModal } from '../components/forms/AssignmentModals';
import { CarSelect, DriverSelect } from '../components/forms/EntitySelects';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar } from '../components/ui/FilterBar';
import { Input, Select } from '../components/ui/Input';
import { useAssignments } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import type { Assignment } from '../types';
import { driverName, formatDateTime, formatNumber } from '../utils/helpers';
import { t } from '../i18n';

export function Assignments() {
  const [filters, setFilters] = useUrlFilters({
    active_only: '',
    car_id: '',
    driver_id: '',
    date_from: '',
    date_to: '',
    page: '1',
    page_size: '20',
  });
  const [creating, setCreating] = useState(false);
  const [returning, setReturning] = useState<Assignment | null>(null);

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useAssignments({
    active_only: filters.active_only === 'true' || undefined,
    car_id: toInt(filters.car_id),
    driver_id: toInt(filters.driver_id),
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
    page,
    page_size: pageSize,
  });

  const columns: Column<Assignment>[] = [
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (a) => (
        <div>
          <Link to={`/cars/${a.car_id}?tab=assignments`} className="font-mono font-semibold text-primary-700 hover:underline">
            {a.car?.license_plate}
          </Link>
          <p className="text-xs text-secondary-500">
            {a.car?.brand} {a.car?.model}
          </p>
        </div>
      ),
    },
    { key: 'driver', header: t('Vodič'), render: (a) => <span className="font-medium">{driverName(a.driver)}</span> },
    { key: 'from', header: t('Prevzaté'), className: 'whitespace-nowrap', render: (a) => formatDateTime(a.assigned_at) },
    {
      key: 'to',
      header: t('Vrátené'),
      className: 'whitespace-nowrap',
      render: (a) => (a.returned_at ? formatDateTime(a.returned_at) : <Badge variant="success" dot>{t('Požičané')}</Badge>),
    },
    {
      key: 'km',
      header: t('Km (začiatok → koniec)'),
      className: 'tabular-nums whitespace-nowrap',
      render: (a) => (
        <>
          {formatNumber(a.start_km)} → {formatNumber(a.end_km)}
          {a.end_km !== null && a.start_km !== null && <span className="ml-1 text-xs text-secondary-500">({formatNumber(a.end_km - a.start_km)} km)</span>}
        </>
      ),
    },
    { key: 'purpose', header: t('Účel'), render: (a) => <span className="line-clamp-2 max-w-xs">{a.purpose || '–'}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right',
      render: (a) =>
        !a.returned_at && (
          <Button size="sm" variant="outline" onClick={() => setReturning(a)}>
            {t('Vrátiť')}
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('Pridelenie vozidiel')}
        subtitle={t('Kto má ktoré vozidlo a história výpožičiek')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />  {t('Prideliť vozidlo')}
          </Button>
        }
      />
      <Card padding="none">
        <FilterBar>
          <Select
            label={t('Stav')}
            wrapperClassName="sm:w-40"
            value={filters.active_only}
            onChange={(e) => setFilters({ active_only: e.target.value })}
            placeholder={t('Všetko')}
            options={[{ value: 'true', label: t('Iba požičané') }]}
          />
          <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
          <DriverSelect
            label={t('Vodič')}
            onlyActive={false}
            wrapperClassName="sm:w-56"
            placeholder={t('Všetci vodiči')}
            value={filters.driver_id}
            onChange={(e) => setFilters({ driver_id: e.target.value })}
          />
          <Input label={t('Od')} type="date" wrapperClassName="sm:w-40" value={filters.date_from} onChange={(e) => setFilters({ date_from: e.target.value })} />
          <Input label={t('Do')} type="date" wrapperClassName="sm:w-40" value={filters.date_to} onChange={(e) => setFilters({ date_to: e.target.value })} />
        </FilterBar>
        <DataTable
          data={data?.assignments}
          columns={columns}
          rowKey={(a) => a.id}
          isLoading={isLoading}
          isFetching={isFetching}
          emptyMessage={<EmptyState icon={<Calendar className="h-10 w-10" />} title={t('Žiadne pridelenia nezodpovedajú filtru')} />}
          pagination={
            data && {
              page,
              pageSize,
              total: data.total,
              onPageChange: (p) => setFilters({ page: String(p) }),
              onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
            }
          }
        />
      </Card>
      <AssignmentCreateModal isOpen={creating} onClose={() => setCreating(false)} />
      <AssignmentReturnModal assignment={returning} onClose={() => setReturning(null)} />
    </>
  );
}
